import { useEffect, useState } from "react";
import { CheckCircle2, KeyRound, ShieldCheck } from "lucide-react";
import { Field, TextInput, TextArea } from "@/components/brand/Field";
import { api, formatApiError } from "@/lib/api";

const btnCls =
  "rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black transition-colors hover:bg-brand-snow disabled:opacity-60";

const Card = ({ title, description, children, testid }) => (
  <div className="surface-card space-y-6 p-6 sm:p-8" data-testid={testid}>
    <div className="space-y-1">
      <h2 className="font-display text-lg font-semibold text-brand-snow">{title}</h2>
      <p className="text-sm text-brand-mist">{description}</p>
    </div>
    {children}
  </div>
);

const AdminSettings = () => {
  const [site, setSite] = useState({ display_email: "", phone: "", address: "", logo_url: "" });
  const [socials, setSocials] = useState({ linkedin: "", x: "", instagram: "", youtube: "" });
  const [secrets, setSecrets] = useState({});
  const [paypal, setPaypal] = useState({ paypal_client_id: "", paypal_client_secret: "" });
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "" });
  const [notice, setNotice] = useState({});
  const [error, setError] = useState({});

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const d = r.data;
      setSite({
        display_email: d.display_email || "",
        phone: d.phone || "",
        address: d.address || "",
        logo_url: d.logo_url || "",
      });
      setSocials({ linkedin: "", x: "", instagram: "", youtube: "", ...(d.socials || {}) });
      setSecrets(d.secrets || {});
    }).catch(() => {});
  }, []);

  const flash = (key, msg) => setNotice((n) => ({ ...n, [key]: msg }));
  const fail = (key, err) => setError((e) => ({ ...e, [key]: formatApiError(err.response?.data?.detail) }));

  const saveSite = async (e) => {
    e.preventDefault();
    setError((x) => ({ ...x, site: "" }));
    try {
      await api.put("/admin/settings", { ...site, socials });
      flash("site", "Site details saved. The website header and footer update on next page load.");
    } catch (err) { fail("site", err); }
  };

  const savePaypal = async (e) => {
    e.preventDefault();
    setError((x) => ({ ...x, paypal: "" }));
    try {
      const payload = Object.fromEntries(Object.entries(paypal).filter(([, v]) => v));
      await api.put("/admin/settings", payload);
      flash("paypal", "Credentials stored securely. Leave fields blank to keep existing values.");
      setPaypal({ paypal_client_id: "", paypal_client_secret: "" });
      const r = await api.get("/admin/settings");
      setSecrets(r.data.secrets || {});
    } catch (err) { fail("paypal", err); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setError((x) => ({ ...x, pw: "" }));
    try {
      await api.post("/auth/change-password", passwords);
      flash("pw", "Password updated. Use it on your next sign-in.");
      setPasswords({ current_password: "", new_password: "" });
    } catch (err) { fail("pw", err); }
  };

  const SecretHint = ({ name }) =>
    secrets[name]?.configured ? (
      <p className="text-xs text-brand-emerald">Configured · ends in {secrets[name].last4}</p>
    ) : (
      <p className="text-xs text-brand-mist">Not configured</p>
    );

  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-settings">
      <div className="space-y-1">
        <h1 className="font-display text-2xl font-bold text-brand-snow sm:text-3xl">Settings</h1>
        <p className="text-sm text-brand-mist">Site details, integrations and account security — no code changes needed.</p>
      </div>

      <form onSubmit={saveSite}>
        <Card title="Website details" description="Contact details, address, social links and logo shown across the public site." testid="settings-site">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Public email" htmlFor="set-email"><TextInput id="set-email" testid="set-email" value={site.display_email} onChange={(e) => setSite({ ...site, display_email: e.target.value })} placeholder="info@a2zrankings.com" /></Field>
            <Field label="Phone" htmlFor="set-phone"><TextInput id="set-phone" testid="set-phone" value={site.phone} onChange={(e) => setSite({ ...site, phone: e.target.value })} placeholder="+91 6389095473" /></Field>
          </div>
          <Field label="Office address" htmlFor="set-address"><TextArea id="set-address" rows={2} testid="set-address" value={site.address} onChange={(e) => setSite({ ...site, address: e.target.value })} /></Field>
          <Field label="Logo image URL" htmlFor="set-logo" hint="Optional — replaces the built-in mark. Use a transparent PNG/SVG."><TextInput id="set-logo" testid="set-logo" value={site.logo_url} onChange={(e) => setSite({ ...site, logo_url: e.target.value })} placeholder="https://…" /></Field>
          <div className="grid gap-5 sm:grid-cols-2">
            {[["linkedin", "LinkedIn URL"], ["x", "X (Twitter) URL"], ["instagram", "Instagram URL"], ["youtube", "YouTube URL"]].map(([k, label]) => (
              <Field key={k} label={label} htmlFor={`set-${k}`}>
                <TextInput id={`set-${k}`} testid={`set-social-${k}`} value={socials[k]} onChange={(e) => setSocials({ ...socials, [k]: e.target.value })} placeholder="https://…" />
              </Field>
            ))}
          </div>
          {notice.site && <p className="flex items-center gap-2 text-sm text-brand-emerald" data-testid="settings-site-saved"><CheckCircle2 size={15} /> {notice.site}</p>}
          {error.site && <p className="text-sm text-red-400">{error.site}</p>}
          <button type="submit" className={btnCls} data-testid="settings-site-save">Save site details</button>
        </Card>
      </form>

      <form onSubmit={savePaypal}>
        <Card title="Payment gateway — PayPal" description="Stored server-side, never shown in full and never exposed to the public site. Leave blank to keep existing values." testid="settings-paypal">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="PayPal Client ID" htmlFor="pp-id">
              <TextInput id="pp-id" testid="paypal-client-id" value={paypal.paypal_client_id} onChange={(e) => setPaypal({ ...paypal, paypal_client_id: e.target.value })} autoComplete="off" />
              <SecretHint name="paypal_client_id" />
            </Field>
            <Field label="PayPal Client Secret" htmlFor="pp-secret">
              <TextInput id="pp-secret" type="password" testid="paypal-client-secret" value={paypal.paypal_client_secret} onChange={(e) => setPaypal({ ...paypal, paypal_client_secret: e.target.value })} autoComplete="new-password" />
              <SecretHint name="paypal_client_secret" />
            </Field>
          </div>
          {notice.paypal && <p className="flex items-center gap-2 text-sm text-brand-emerald" data-testid="settings-paypal-saved"><CheckCircle2 size={15} /> {notice.paypal}</p>}
          {error.paypal && <p className="text-sm text-red-400">{error.paypal}</p>}
          <button type="submit" className={btnCls} data-testid="settings-paypal-save">Save credentials</button>
        </Card>
      </form>

      <form onSubmit={changePassword}>
        <Card title="Admin account" description="Change the password for the signed-in administrator." testid="settings-account">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Current password" htmlFor="pw-current"><TextInput id="pw-current" type="password" testid="pw-current" value={passwords.current_password} onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} autoComplete="current-password" required /></Field>
            <Field label="New password" htmlFor="pw-new" hint="Minimum 10 characters."><TextInput id="pw-new" type="password" testid="pw-new" value={passwords.new_password} onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} autoComplete="new-password" minLength={10} required /></Field>
          </div>
          {notice.pw && <p className="flex items-center gap-2 text-sm text-brand-emerald" data-testid="settings-pw-saved"><CheckCircle2 size={15} /> {notice.pw}</p>}
          {error.pw && <p className="text-sm text-red-400" data-testid="settings-pw-error">{error.pw}</p>}
          <button type="submit" className={btnCls} data-testid="settings-pw-save">
            <KeyRound size={14} className="mr-2 inline" /> Update password
          </button>
          <p className="flex items-center gap-2 text-xs text-brand-mist"><ShieldCheck size={13} className="text-brand-emerald" /> Forgotten passwords reset via a one-hour email link from the sign-in page.</p>
        </Card>
      </form>
    </div>
  );
};

export default AdminSettings;
